import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// The store embed runs this same client inside someone else's website.
// Don't persist a Nexus login there, and don't rewrite that site's URL.
const embedOnHostPage =
  typeof window !== 'undefined' && window.__NEXUS_EMBED__ === true;

const embedMemory = new Map<string, string>();
const embedMemoryStorage = {
  getItem: (key: string) => (embedMemory.has(key) ? embedMemory.get(key)! : null),
  setItem: (key: string, value: string) => {
    embedMemory.set(key, value);
  },
  removeItem: (key: string) => {
    embedMemory.delete(key);
  },
};

// Create Supabase client with tab-switching optimizations
export const supabase: SupabaseClient<Database> = createClient(
  supabaseUrl, 
  supabaseAnonKey, 
  {
    auth: embedOnHostPage
      ? {
          autoRefreshToken: false,
          persistSession: false,
          detectSessionInUrl: false,
          storage: embedMemoryStorage,
          storageKey: 'nexus-embed-auth',
        }
      : {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
          storageKey: 'nexus-booking-auth',
        },
    realtime: {
      // CRITICAL: Disable auto-reconnection on page focus to prevent tab switch reloads
      params: {
        // A bot turn writes several session, message, and chat rows in one burst.
        // A cap of 2 dropped the "finished" update, so the green dot stayed on.
        eventsPerSecond: 20,
      },
      // Disable automatic reconnection on visibility change
      heartbeatIntervalMs: 30000,   // 30 seconds instead of default 15
      reconnectAfterMs: (tries: number) => [1000, 2000, 5000, 10000][tries - 1] || 10000,
    },
    global: {
      headers: {
        'X-Client-Info': 'nexus-booking-app'
      }
    }
  }
);

// Postgres change events are dropped when the socket is still on the anon key.
// Keep it on the signed-in session, including after a token refresh.
function syncRealtimeAuth(token: string | undefined) {
  const realtime = supabase.realtime as { setAuth?: (accessToken: string | null) => void | Promise<void> };
  if (typeof realtime.setAuth !== 'function') return;
  void realtime.setAuth(token ?? null);
}

void supabase.auth.getSession().then(({ data }) => {
  syncRealtimeAuth(data.session?.access_token);
});

supabase.auth.onAuthStateChange((_event, session) => {
  syncRealtimeAuth(session?.access_token);
});